/**
 * ShqEnv
 *
 * @copyright   Copyright (c) 2026 Zowta Ltd (http://www.shipperhq.com)
 *              Copyright, 2026, Zowta, LLC - US license
 */

import { useState } from 'react'
import { axiosClient } from 'net/ajax'

// Flash messages arrive as [[type, message], ...] pairs (e.g. Devise's
// "You have to confirm your account before continuing." alert on an
// unconfirmed login). Surface the non-notice ones so the form can display
// them instead of silently swallowing the response. Returns null when there
// is nothing to show.
function extractFlashAlerts(flash) {
    if (!Array.isArray(flash)) {
        return null
    }

    const messages = flash
        .filter((entry) => Array.isArray(entry) && entry[0] !== 'notice')
        .map((entry) => entry[1])
        .filter(Boolean)

    return messages.length ? messages.join(' ') : null
}

export default function useSubmitForm(url, method = 'post') {
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(false)

    return {
        submit: async (data) => {
            setLoading(true)

            try {
                const result = await axiosClient[method](url, data)
                if (result && result.status >= 200 && result.status < 300) {
                    if (result.data && result.data.success === false) {
                        if (Array.isArray(result.data.errors)) {
                            setError(result.data.errors.join(', '))
                        } else {
                            setError(
                                Object.entries(result.data.errors)
                                    .map(
                                        ([key, value]) =>
                                            `${key}: ${String(value)}`
                                    )
                                    .join(', ')
                            )
                        }
                        setLoading(false)
                        return false
                    } else {
                        // A 2xx that isn't an explicit success can still carry a
                        // flash alert (e.g. a redirect back to /login for an
                        // unconfirmed account). Show it rather than swallow it.
                        const flashAlert =
                            result.data && result.data.success !== true
                                ? extractFlashAlerts(result.data.flash)
                                : null
                        setError(flashAlert)
                        setLoading(false)
                        return result.data
                    }
                }

                setError('Error submitting the form.')
            } catch (e) {
                if (
                    e.response &&
                    e.response.data &&
                    (e.response.data.errors || e.response.data.error)
                ) {
                    if (e.response.data.error) {
                        setError(e.response.data.error)
                    } else {
                        setError(
                            Object.entries(e.response.data.errors)
                                .map(
                                    ([key, value]) => `${key}: ${String(value)}`
                                )
                                .join(', ')
                        )
                    }
                } else {
                    setError(e.message)
                }
            }

            setLoading(false)

            return false
        },
        error,
        loading,
    }
}
